import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchLessonById, fetchLessonsByCourseId, fetchQuizQuestions } from '@/services/lessons';
import { completeLesson, getLessonProgress } from '@/services/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import QuizQuestion from '@/components/ui/quiz/QuizQuestion';
import { QuizData } from '@/components/ui/content-editor/types';
import Navbar from '@/components/layout/Navbar';
import { toast } from 'sonner';
import { api } from '@/services/api';
import CustomYouTubePlayer from "@/components/ui/CustomYouTubePlayer";
import CustomImageViewer from '@/components/ui/CustomImageViewer';
import ReactDOM from 'react-dom/client';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { applyStudentViewOrdering } from '@/utils/quiz';

// Lesson data structure
interface LessonContent {
  id: string;
  title: string;
  courseId: string;
  courseName: string;
  content: string | null; // Text content
  quiz: QuizData;
  orderIndex: number;
  isCompleted: boolean;
  completedAt: string | null;
  nextLessonId?: string;
  prevLessonId?: string;
}

const Lesson: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<LessonContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState<{ score: number; total: number } | null>(null);
  
  // Use the auth redirect hook
  useAuthRedirect(false);

  // Fetch lesson data effect
  useEffect(() => {
    const fetchLessonData = async () => {
      if (!id || !user) {
        // If user is null but isAuthenticated is true (shouldn't happen often, but guard against it)
        // or if id is missing, we might still be loading or have an issue.
        // Let the loading state handle it, or potentially show an error if user remains null.
        // The auth hook handles the primary redirect case.
        return;
      }
      
      setIsLoading(true);
      try {
        // Fetch lesson data from API
        const lessonData = await fetchLessonById(id);
        
        if (!lessonData) {
          toast.error('Lesson not found');
          navigate('/dashboard');
          return;
        }
        
        // Check if lesson is completed from the new API response
        const isCompleted = lessonData.isCompleted || false;
        const completedAt = lessonData.completedAt;
        
        // Get all lessons and course data for the course to determine next/prev and get course title
        const courseResponse = await api.get<any>(`/api/Course/${lessonData.courseId}`);
        const courseData = courseResponse.data;
        let courseName = 'Course';
        
        // Extract course name from courseData if available
        if (courseData && courseData.title) {
          courseName = courseData.title;
        }
        
        // Get the lesson array from the course data
        const allLessons = courseData.lessons || await fetchLessonsByCourseId(lessonData.courseId);
        
        // Sort lessons by orderIndex
        const sortedLessons = [...allLessons].sort((a, b) => 
          (a.orderIndex || 0) - (b.orderIndex || 0)
        );
        
        // Find current lesson index
        const currentIndex = sortedLessons.findIndex(l => l.id === id);
        
        // Determine next and previous lessons
        const nextLesson = currentIndex < sortedLessons.length - 1 ? sortedLessons[currentIndex + 1] : null;
        const prevLesson = currentIndex > 0 ? sortedLessons[currentIndex - 1] : null;
        
        // Use quiz questions from the lesson data if available
        let quizData: QuizData = { questions: [] };
        if (lessonData.quizQuestions && lessonData.quizQuestions.length > 0) {
          quizData = {
            questions: lessonData.quizQuestions.map(q => {
              // Handle potential type mismatch
              const questionObj = q as any;
              return {
                id: questionObj.id,
                question: questionObj.question || questionObj.questionText || '',
                options: questionObj.options || 
                  (questionObj.answers ? questionObj.answers.map((a: any) => ({
                    id: a.id,
                    text: a.text || a.answerText || '',
                    isCorrect: a.isCorrect
                  })) : [])
              };
            })
          };
          
          // Apply student view ordering (randomize questions, sort answers by ID)
          quizData = applyStudentViewOrdering(quizData);
        } else {
          // Fallback to separate API call if not included
          try {
            const quizQuestions = await fetchQuizQuestions(id);
            if (quizQuestions && quizQuestions.length > 0) {
              quizData = {
                questions: quizQuestions.map(q => ({
                  id: q.id,
                  question: q.question,
                  options: q.options
                }))
              };
              
              // Apply student view ordering (randomize questions, sort answers by ID)
              quizData = applyStudentViewOrdering(quizData);
            }
          } catch (error) {
            console.error('Error fetching quiz questions:', error);
          }
        }
        
        // Create lesson object
        const lessonContent: LessonContent = {
          id: lessonData.id,
          title: lessonData.title,
          courseId: lessonData.courseId,
          courseName: courseName,
          content: lessonData.content || null,
          quiz: quizData,
          orderIndex: lessonData.orderIndex || 0,
          isCompleted,
          completedAt,
          nextLessonId: nextLesson?.id,
          prevLessonId: prevLesson?.id
        };

        console.log('Lesson Content with Quiz Data:', lessonContent);
        
        setLesson(lessonContent);
        setQuizCompleted(isCompleted);
      } catch (error) {
        console.error('Error loading lesson:', error);
        toast.error('Failed to load lesson data');
      } finally {
        setIsLoading(false);
      }
    };
    
    // Only fetch if authenticated (user object exists) and id is present
    if (isAuthenticated && user && id) {
       fetchLessonData();
    } else if (!isAuthenticated) {
      // If not authenticated, the hook should have already initiated a redirect.
      // We set loading to false to avoid showing the loading spinner indefinitely.
      setIsLoading(false);
    }
    // Keep dependencies needed for fetching data, remove isAuthenticated if no longer directly used inside
  }, [id, navigate, user, isAuthenticated]); // Pass isAuthenticated to the dependency array

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <div className="flex flex-col items-center justify-center h-80">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading lesson...</p>
          </div>
        </main>
      </div>
    );
  }
  
  // Handle marking lesson as complete
  const markLessonAsComplete = async () => {
    if (!user || !lesson) return;
    
    try {
      // Call the API to mark the lesson as complete
      await completeLesson(lesson.id);
      
      // Update the lesson state
      setLesson({
        ...lesson,
        isCompleted: true,
      });
      
      setQuizCompleted(true);
      
      toast.success('Lesson completed successfully!');
    } catch (error) {
      console.error("Failed to save lesson completion:", error);
      toast.error('Failed to save your progress');
    }
  };
  
  // Handle quiz completion
  const handleQuizComplete = async (score: number, total: number) => {
    if (!user || !lesson) return;
    
    setQuizCompleted(true);
    setQuizScore({ score, total });
    
    // Mark the lesson as completed
    await markLessonAsComplete();
  };
  
  // Navigate to next lesson
  const goToNextLesson = () => {
    if (lesson.nextLessonId) {
      navigate(`/lesson/${lesson.nextLessonId}`);
    } else {
      // If there's no next lesson, go to the course completion page
      navigate(`/course/${lesson.courseId}`);
    }
  };
  
  // Navigate to previous lesson
  const goToPrevLesson = () => {
    if (lesson.prevLessonId) {
      navigate(`/lesson/${lesson.prevLessonId}`);
    }
  };
  
  // Extract YouTube video ID from a URL
  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    
    // Handle youtu.be and youtube.com URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Render content
  const renderContent = () => {
    const content = lesson.content;
    
    if (!content) {
      return <div className="space-y-8"></div>;
    }
    
    // Process video tags first to prevent conflicts with other markdown processing
    let processedContent = content;
    
    // Try to find any format of !video[...](...)
    processedContent = processedContent.replace(/!video\s*\[(.*?)\]\s*\((.*?)\)/g, (match, title, url) => {
      const videoId = getYouTubeVideoId(url);
      if (videoId) {
        try {
          // Base64 encode the title to handle spaces and special characters
          const encodedTitle = btoa(unescape(encodeURIComponent(title)));
          return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
        } catch (e) {
          console.error("Error encoding video title:", e);
          return `VIDEO_EMBED_${videoId}_TITLE_Video`;
        }
      }
      return match; // Keep the original if not a valid video
    });
    
    // Handle malformed !video tags - specifically when there's no space between !video and [title]
    processedContent = processedContent.replace(/!video(\[(.*?)\]\((.*?)\))/g, (match, bracketContent, title, url) => {
      const videoId = getYouTubeVideoId(url);
      if (videoId) {
        try {
          // Base64 encode the title to handle spaces and special characters
          const encodedTitle = btoa(unescape(encodeURIComponent(title)));
          return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
        } catch (e) {
          console.error("Error encoding video title:", e);
          return `VIDEO_EMBED_${videoId}_TITLE_Video`;
        }
      }
      return match;
    });
    
    // Catch any other potential !video tags without proper formatting
    processedContent = processedContent.replace(/!video(\[|)([^[(]*?)(\]|)(\(|)([^()]*?)(\)|)/g, (match, openBracket, title, closeBracket, openParen, url, closeParen) => {
      // If this is a properly formatted tag that was already processed, skip it
      if (match.match(/!video\s*\[\s*.+?\s*\]\s*\(\s*.+?\s*\)/) || match.match(/!video\[.+?\]\(.+?\)/)) {
        return match;
      }
      
      // Try to extract a URL from anywhere in the match
      const urlMatch = match.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch) {
        const extractedUrl = urlMatch[1];
        const videoId = getYouTubeVideoId(extractedUrl);
        if (videoId) {
          // Use the text before the URL as the title, or "Video" if none
          const extractedTitle = title.trim() || "Video";
          try {
            // Base64 encode the title to handle spaces and special characters
            const encodedTitle = btoa(unescape(encodeURIComponent(extractedTitle)));
            return `VIDEO_EMBED_${videoId}_TITLE_${encodedTitle}`;
          } catch (e) {
            console.error("Error encoding video title:", e);
            return `VIDEO_EMBED_${videoId}_TITLE_Video`;
          }
        }
      }
      return match;
    });
    
    // Also check for direct YouTube links in the content
    processedContent = processedContent.replace(/(\s|^)(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(\s|$)/g, 
      (match, before, protocol, www, domain, videoId, after) => {
        const fullUrl = `${protocol || 'https://'}${www || ''}${domain}${videoId}`;
        const extractedId = getYouTubeVideoId(fullUrl);
        if (extractedId) {
          try {
            // Keep the surrounding whitespace
            const encodedTitle = btoa(unescape(encodeURIComponent("Video")));
            return `${before}VIDEO_EMBED_${extractedId}_TITLE_${encodedTitle}${after}`;
          } catch (e) {
            console.error("Error encoding video title:", e);
            return `${before}VIDEO_EMBED_${extractedId}_TITLE_Video${after}`;
          }
        }
        return match;
      }
    );
    
    // Process images - replace standard markdown image syntax with custom placeholders
    processedContent = processedContent.replace(/!\[(.+?)\]\((.+?)\)/g, (match, alt, src) => {
      try {
        // Base64 encode the alt and src to handle spaces and special characters
        const encodedAlt = btoa(unescape(encodeURIComponent(alt || '')));
        const encodedSrc = btoa(unescape(encodeURIComponent(src)));
        return `IMAGE_EMBED_${encodedSrc}_ALT_${encodedAlt}`;
      } catch (e) {
        console.error("Error encoding image data:", e);
        return match; // Keep the original if there's an error
      }
    });
    
    // Now process the rest of markdown
    processedContent = processedContent
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/__(.+?)__/g, '<u>$1</u>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/```(.+?)```/gs, '<pre><code>$1</code></pre>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
      .replace(/!\[(.+?)\]\((.+?)\)/g, '<img src="$2" alt="$1" class="rounded-md max-h-96 my-4">') // This line will be skipped due to our preprocessing
      .replace(/^>\s(.+)$/gm, '<blockquote class="border-l-4 border-primary pl-4 italic my-4">$1</blockquote>')
      // Handle table rendering
      .replace(/(\|[^\n]+\|\n)(\|[-|]+\|\n)(\|[^\n]+\|.+)/gs, (match) => {
        const rows = match.split('\n').filter(row => row.trim() !== '');
        let tableHtml = '<table class="border-collapse w-full my-6"><thead>';
        
        // Header row
        if (rows[0]) {
          tableHtml += '<tr>';
          const cells = rows[0].split('|').filter(cell => cell.trim() !== '');
          cells.forEach(cell => {
            tableHtml += `<th class="border border-gray-300 dark:border-gray-600 p-2">${cell.trim()}</th>`;
          });
          tableHtml += '</tr></thead><tbody>';
        }
        
        // Skip the separator row (rows[1])
        
        // Content rows
        for (let i = 2; i < rows.length; i++) {
          tableHtml += '<tr>';
          const cells = rows[i].split('|').filter(cell => cell.trim() !== '');
          cells.forEach(cell => {
            tableHtml += `<td class="border border-gray-300 dark:border-gray-600 p-2">${cell.trim()}</td>`;
          });
          tableHtml += '</tr>';
        }
        
        tableHtml += '</tbody></table>';
        return tableHtml;
      });
    
    // Finally, replace the video placeholders with actual iframe embeds
    processedContent = processedContent.replace(/VIDEO_EMBED_([a-zA-Z0-9_-]+)_TITLE_([^<\s]+)(\s|$|\<)/g, (match, videoId, encodedTitle, trailingSpace) => {
      // Decode the title from base64
      let title;
      try {
        title = decodeURIComponent(escape(atob(encodedTitle)));
      } catch (e) {
        console.error("Error decoding title:", e, encodedTitle);
        title = "Video"; // Fallback if decoding fails
      }
      
      // Return a div with a data attribute that will be replaced with the actual component
      // Important: Don't include the trailing space inside the div to avoid BR rendering issues
      return `<div class="custom-youtube-player" data-video-id="${videoId}" data-video-title="${encodeURIComponent(title)}"></div>${trailingSpace || ''}`;
    });
    
    // Replace image placeholders with custom image viewer
    processedContent = processedContent.replace(/IMAGE_EMBED_([^_]+)_ALT_([^<\s]+)(\s|$|\<)/g, (match, encodedSrc, encodedAlt, trailingSpace) => {
      // Decode the src and alt from base64
      let src, alt;
      try {
        src = decodeURIComponent(escape(atob(encodedSrc)));
        alt = decodeURIComponent(escape(atob(encodedAlt)));
      } catch (e) {
        console.error("Error decoding image data:", e, encodedSrc, encodedAlt);
        return `<img src="${encodedSrc}" alt="Image" class="rounded-md max-h-96 my-4">${trailingSpace || ''}`; // Fallback if decoding fails
      }
      
      // Return a div with data attributes that will be replaced with the actual component
      return `<div class="custom-image-viewer" data-image-src="${encodeURIComponent(src)}" data-image-alt="${encodeURIComponent(alt)}"></div>${trailingSpace || ''}`;
    });
    
    return (
      <div className="space-y-8">
        {/* Text content with embedded videos and images */}
        {processedContent && (
          <div className="prose prose-slate max-w-none dark:prose-invert">
            <div 
              dangerouslySetInnerHTML={{ __html: processedContent }} 
              ref={(el) => {
                // Replace placeholder divs with actual CustomYouTubePlayer components
                if (el) {
                  const playerDivs = el.querySelectorAll('.custom-youtube-player');
                  playerDivs.forEach(div => {
                    const videoId = div.getAttribute('data-video-id');
                    const encodedTitle = div.getAttribute('data-video-title');
                    
                    if (videoId && !div.querySelector('#player-' + videoId)) {
                      // Create container for React to render into
                      const container = document.createElement('div');
                      div.appendChild(container);
                      
                      // Render the custom YouTube player component into the container
                      const title = encodedTitle ? decodeURIComponent(encodedTitle) : 'Video';
                      const root = ReactDOM.createRoot(container);
                      root.render(
                        <CustomYouTubePlayer 
                          videoId={videoId}
                          title={title}
                        />
                      );
                    }
                  });

                  // Replace placeholder divs with actual CustomImageViewer components
                  const imageDivs = el.querySelectorAll('.custom-image-viewer');
                  imageDivs.forEach(div => {
                    const encodedSrc = div.getAttribute('data-image-src');
                    const encodedAlt = div.getAttribute('data-image-alt');
                    
                    if (encodedSrc && !div.querySelector('.image-wrapper-container')) {
                      // Create container for React to render into
                      const container = document.createElement('div');
                      div.appendChild(container);
                      
                      // Render the custom image viewer component into the container
                      const src = encodedSrc ? decodeURIComponent(encodedSrc) : '';
                      const alt = encodedAlt ? decodeURIComponent(encodedAlt) : 'Image';
                      const root = ReactDOM.createRoot(container);
                      root.render(
                        <CustomImageViewer 
                          src={src}
                          alt={alt}
                        />
                      );
                    }
                  });
                }
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const fullWidthQuiz = !lesson.content && lesson.quiz.questions.length > 1;

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to={`/course/${lesson.courseId}`} className="inline-flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Course
            </Link>
          </Button>
          
          <div className="text-sm text-muted-foreground">
            Lesson {lesson.orderIndex + 1}
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {fullWidthQuiz ? (
            <div className="lg:col-span-12 space-y-6">
              {/* Quiz area (same as the original sidebar quiz area code) */}
              {lesson.quiz.questions.length > 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold">{lesson.title}</h2>
                      <p className="text-muted-foreground">
                        Complete the quiz to mark this lesson as finished.
                      </p>
                    </div>
                    {!quizCompleted ? (
                      <QuizQuestion 
                        quiz={{ questions: lesson.quiz.questions }}
                        onComplete={handleQuizComplete}
                      />
                    ) : (
                      <div className="text-center p-6 bg-primary/10 rounded-md">
                        <CheckCircle className="h-12 w-12 mx-auto text-primary" />
                        <h3 className="mt-2 text-xl font-bold">Quiz Completed</h3>
                        {quizScore && (
                          <p className="mt-1">
                            You scored {quizScore.score} out of {quizScore.total}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="p-6">
                  <h3 className="text-xl font-bold mb-4">Lesson Progress</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Status:</span>
                      <Badge variant={lesson.isCompleted ? "success" : "outline"}>
                        {lesson.isCompleted ? "Completed" : "In Progress"}
                      </Badge>
                    </div>
                  </div>
                </Card>
              )}
              {/* Mobile Navigation Buttons (if any) remain here */}
              <div className="lg:hidden space-y-2">
                {/* ... existing mobile navigation buttons ... */}
              </div>
            </div>
          ) : (
            <>
              <div className="lg:col-span-8">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold tracking-tight">{lesson.title}</h1>
                  <p className="mt-2 text-muted-foreground">
                    Course: {lesson.courseName}
                  </p>
                </div>
                
                <div className="space-y-8">
                  {renderContent()}
                  
                  {/* Mark as Complete Section - Only show if there's no quiz and lesson is not completed */}
                  {lesson.quiz.questions.length === 0 && !lesson.isCompleted && (
                    <Card className="p-6">
                      <div className="flex flex-col items-center justify-center">
                        <p className="mb-4 text-center text-muted-foreground">
                          Mark this lesson as complete to track your progress
                        </p>
                        <Button onClick={markLessonAsComplete} size="lg">
                          Mark as Complete
                        </Button>
                      </div>
                    </Card>
                  )}
                  
                  {/* Navigation Buttons */}
                  <div className="flex justify-between mt-6">
                    {lesson.prevLessonId ? (
                      <Button 
                        variant="outline" 
                        onClick={goToPrevLesson}
                        className="flex items-center"
                      >
                        <ArrowLeft className="mr-1 h-4 w-4" /> Previous Lesson
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        asChild
                      >
                        <Link to={`/course/${lesson.courseId}`} className="flex items-center">
                          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Course
                        </Link>
                      </Button>
                    )}
                    
                    {lesson.isCompleted && lesson.nextLessonId && (
                      <Button 
                        onClick={goToNextLesson} 
                        className="flex items-center"
                      >
                        Next Lesson <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-4 space-y-6">
                {/* Original Quiz Sidebar Area */}
                {lesson.quiz.questions.length > 0 ? (
                  <Card>
                    <CardContent className="p-6">
                      <div className="mb-6">
                        <h2 className="text-2xl font-bold">Quiz</h2>
                        <p className="text-muted-foreground">
                          Complete the quiz to mark this lesson as finished.
                        </p>
                      </div>
                      {!quizCompleted ? (
                        <QuizQuestion 
                          quiz={{ questions: lesson.quiz.questions }}
                          onComplete={handleQuizComplete}
                        />
                      ) : (
                        <div className="text-center p-6 bg-primary/10 rounded-md">
                          <CheckCircle className="h-12 w-12 mx-auto text-primary" />
                          <h3 className="mt-2 text-xl font-bold">Quiz Completed</h3>
                          {quizScore && (
                            <p className="mt-1">
                              You scored {quizScore.score} out of {quizScore.total}
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4">Lesson Progress</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span>Status:</span>
                        <Badge variant={lesson.isCompleted ? "success" : "outline"}>
                          {lesson.isCompleted ? "Completed" : "In Progress"}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                )}
                {/* Mobile Navigation Buttons */}
                <div className="lg:hidden space-y-2">
                  {/* ... existing mobile navigation buttons ... */}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Lesson;
